import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { RecordingStatus } from '../types'
import { api } from './client'

export interface RecordingItem {
  id: string
  call_id: string
  status: RecordingStatus
  track: string
  relative_path: string
  duration_seconds: number
  size_bytes: number
  recorded_at: string | null
  created_at: string
}

export interface RecordingList {
  items: RecordingItem[]
  total: number
  page: number
  page_size: number
}

export interface DiskStats {
  enabled: boolean
  free_bytes: number
  total_bytes: number
  recordings_bytes: number
  skip_new_recordings: boolean
}

interface ListParams {
  page?: number
  pageSize?: number
  status?: string
  dateFrom?: string
  dateTo?: string
}

function buildListQuery(params: ListParams): string {
  const q = new URLSearchParams()
  if (params.page) q.set('page', String(params.page))
  if (params.pageSize) q.set('page_size', String(params.pageSize))
  if (params.status) q.set('status', params.status)
  if (params.dateFrom) q.set('date_from', params.dateFrom)
  if (params.dateTo) q.set('date_to', params.dateTo)
  const s = q.toString()
  return s ? `?${s}` : ''
}

export function useRecordings(params: ListParams = {}) {
  return useQuery({
    queryKey: ['recordings', params],
    queryFn: () => api.get<RecordingList>(`/api/v1/recordings${buildListQuery(params)}`),
    // Recordings list changes when a call ends + merge completes; 30s cache
    // is fine for admin browsing, they can pull-to-refresh mentally.
    staleTime: 30_000,
  })
}

export function useDiskStats() {
  return useQuery({
    queryKey: ['recordings', 'disk-stats'],
    queryFn: () => api.get<DiskStats>('/api/v1/recordings/disk-stats'),
    refetchInterval: 30_000,
  })
}

export function useDeleteRecording() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (callId: string) => api.delete(`/api/v1/recordings/${encodeURIComponent(callId)}`),
    onSuccess: () => {
      // Invalidate BOTH the dedicated recordings list AND the calls list —
      // /staff/calls inlines a RecordingSummary on each row, so removing
      // a recording must refresh that cache too, otherwise the player on
      // Call History would stay pointed at a 404'd stream URL.
      qc.invalidateQueries({ queryKey: ['recordings'] })
      qc.invalidateQueries({ queryKey: ['calls'] })
    },
  })
}

export interface BulkDeleteResult {
  results: Array<{ call_id: string; status: string; error?: string }>
}

export function useBulkDeleteRecordings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (callIds: string[]) =>
      api.post<BulkDeleteResult>('/api/v1/recordings/bulk-delete', {
        call_ids: callIds,
      }),
    onSuccess: () => {
      // Invalidate BOTH the dedicated recordings list AND the calls list —
      // /staff/calls inlines a RecordingSummary on each row, so removing
      // a recording must refresh that cache too, otherwise the player on
      // Call History would stay pointed at a 404'd stream URL.
      qc.invalidateQueries({ queryKey: ['recordings'] })
      qc.invalidateQueries({ queryKey: ['calls'] })
    },
  })
}

/**
 * Build a same-origin URL for the audio element. Auth rides on the session
 * cookie (credentials: 'include' is the default on <audio> same-origin
 * requests). No signed URL needed — backend validates via CurrentAdminDep.
 */
export function streamUrl(callId: string): string {
  return `/api/v1/recordings/${encodeURIComponent(callId)}/stream`
}
